"use client"

import React, { useEffect, useState } from "react"
import SearchInput, { SearchInputProps } from "./SearchInput"
import { usePlaceSearchStore, LocationData } from "@/stores/places"
import useDebounce from "@/hooks/useDebounce"
import { cn } from '@/utils/index'

function formatSuggestionDisplay(
  userInput: string,
  postalCode: string | undefined,
  country: string | undefined
): string {
  const u = userInput.trim()
  if (!u) return u

  const pc = postalCode?.trim()
  const ctry = country?.trim()

  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const hasZip = pc ? new RegExp(`\\b${esc(pc)}\\b`).test(u) : false

  const partList = u.split(",").map((p) => p.trim().toLowerCase())
  const hasCountry = ctry ? partList.some((p) => p === ctry.toLowerCase()) : false

  const segments: string[] = [u]
  if (pc && !hasZip) segments.push(pc)
  if (ctry && !hasCountry) segments.push(ctry)

  return segments.join(", ")
}

const PlaceSearchInput = React.forwardRef<HTMLInputElement, SearchInputProps & {
  value: string
  onChange: (location: LocationData) => void
}>(({
  value,
  onChange,
  borderless,
  className,
  dashboardInput,
}, ref) => {
  const [showSuggestion, setShowSuggestion] = useState(false)
  
  // Isolated state: prevents data leakage between forms/users
  const [localQuery, setLocalQuery] = useState(value || "")

  const {
    suggestions,
    fetchSuggestions,
    setSelectedLocation,
    clearLocationData,
  } = usePlaceSearchStore()

  // High-fidelity sync from RHF/Server
  useEffect(() => {
    if (value !== undefined && value !== localQuery) {
      setLocalQuery(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const debouncedQuery = useDebounce(localQuery, 400)

  // Fetch suggestions based on local typing
  useEffect(() => {
    if (debouncedQuery.trim().length > 2 && debouncedQuery !== value) {
      fetchSuggestions(debouncedQuery)
    }
  }, [debouncedQuery, fetchSuggestions, value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    setLocalQuery(text)
    setShowSuggestion(true)
  }

  const handleSelect = (location: LocationData) => {
    setLocalQuery(location.address)
    setShowSuggestion(false)
    setSelectedLocation(location)
    onChange(location) // Commit to RHF
  }

  const handleClear = () => {
    setLocalQuery("")
    clearLocationData()
    onChange({
      address: "",
      city: undefined,
      state: undefined,
      country: undefined,
      coordinates: { lat: 0, lon: 0 },
    })
  }

  return (
    <div className="relative flex flex-col">
      <SearchInput
        ref={ref}
        value={localQuery}
        onChange={handleInputChange}
        className={cn("property-search-form-field", className)}
        placeholder="Search for places"
        borderless={borderless}
        dashboardInput={dashboardInput}
      />
      {localQuery.length > 0 && (
        <button
          onClick={handleClear}
          type="button"
          className="absolute right-3 top-2.5 z-10 text-gray-400 hover:text-gray-700"
          aria-label="Clear search input"
        >
          ✕
        </button>
      )}

      {suggestions.length > 0 && showSuggestion && (
        <div className="absolute top-full z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {suggestions.map((s, idx) => {
            const address = formatSuggestionDisplay(localQuery, s.postal_code, s.country)
            return (
              <div
                key={idx}
                className="cursor-pointer px-4 py-2 text-sm text-gray-400 lg:text-gray-800 dark:bg-gray-900 dark:text-gray-200 hover:bg-gray-100"
                onClick={() => handleSelect({ ...s, address })}
              >
                {address}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
})

export default PlaceSearchInput
